import { useCallback, useEffect, useRef, useState } from 'react';
import { IS_MCP_ENABLED } from '../../../config/appConfig';
import useResponsive from '../../../hooks/useResponsive';
import { getUser } from '../../../services/authService';
import { ChatAllowedRoles, clampChatPanelWidth } from './chatConfig';
import { readPanel, writePanel } from './chatStorage';

// Owned by DashboardLayout: the navbar has to give up exactly the width the
// panel takes, so both read the same piece of state.
export const useChatPanelState = () => {
	const role = getUser()?.role;
	const isAllowed =
		IS_MCP_ENABLED && Boolean(role) && ChatAllowedRoles.includes(role);
	const isDesktop = useResponsive('up', 'lg');

	// Read storage once per mount rather than on every render.
	const restoredRef = useRef(null);
	if (!restoredRef.current) {
		restoredRef.current = readPanel();
	}

	const [isOpen, setIsOpen] = useState(
		() => isAllowed && restoredRef.current.isOpen
	);
	const [width, setWidth] = useState(() => restoredRef.current.width);
	const [isResizing, setIsResizing] = useState(false);

	// Not written mid-drag: that would be a synchronous storage write per frame.
	// Releasing the edge clears isResizing and saves the settled width.
	useEffect(() => {
		if (isAllowed && !isResizing) {
			writePanel({ isOpen, width });
		}
	}, [isAllowed, isOpen, width, isResizing]);

	// A width dragged out on a wide monitor would swallow a smaller row.
	useEffect(() => {
		const onWindowResize = () =>
			setWidth((current) => clampChatPanelWidth(current));

		window.addEventListener('resize', onWindowResize);
		return () => window.removeEventListener('resize', onWindowResize);
	}, []);

	const toggle = useCallback(() => setIsOpen((current) => !current), []);
	const close = useCallback(() => setIsOpen(false), []);

	const resize = useCallback(
		(next) => setWidth(clampChatPanelWidth(next)),
		[]
	);
	const startResize = useCallback(() => setIsResizing(true), []);
	const endResize = useCallback(() => setIsResizing(false), []);

	const open = isAllowed && isOpen;

	return {
		isAllowed,
		isOpen: open,
		width,
		// As a drawer below the breakpoint it reserves no width in the layout.
		offsetWidth: open && isDesktop ? width : 0,
		isResizing,
		toggle,
		close,
		resize,
		startResize,
		endResize,
	};
};
